import React, { useState } from 'react';
import { Textarea } from '../../../shared/components/Textarea';
import { validateDecisao } from '../validation/recrutamentoForms';
import { APPROVAL_STEP_LABELS } from './format';
import { RecrutamentoModal } from './RecrutamentoModal';

export type DecisaoTipo = 'aprovar' | 'reprovar' | 'devolver';

interface DecisaoModalProps {
  open: boolean;
  tipo: DecisaoTipo;
  stepKey: string;
  code: string;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (parecer?: string) => void;
}

const TITULOS: Record<DecisaoTipo, string> = {
  aprovar: 'Aprovar requisição',
  reprovar: 'Reprovar requisição',
  devolver: 'Devolver ao solicitante',
};

const DESCRICOES: Record<DecisaoTipo, string> = {
  aprovar: 'A requisição segue para o próximo passo da cadeia.',
  reprovar: 'A requisição é encerrada e o solicitante é avisado. O parecer é obrigatório.',
  devolver:
    'A requisição volta ao solicitante para correção e, ao ser reenviada, a cadeia reinicia do primeiro passo. O parecer é obrigatório.',
};

/**
 * Diálogo de decisão da cadeia de aprovação.
 *
 * O parecer é exigido no cliente para reprovar e devolver — o servidor
 * também exige, e a validação aqui existe para não gastar um round-trip
 * mostrando um 400 que a tela já sabia.
 */
export function DecisaoModal({
  open,
  tipo,
  stepKey,
  code,
  loading,
  error,
  onCancel,
  onConfirm,
}: DecisaoModalProps) {
  const [parecer, setParecer] = useState('');
  const [touched, setTouched] = useState(false);

  React.useEffect(() => {
    if (open) {
      setParecer('');
      setTouched(false);
    }
  }, [open, tipo]);

  const erroParecer = validateDecisao(tipo, parecer);
  const parecerObrigatorio = tipo !== 'aprovar';

  return (
    <RecrutamentoModal
      open={open}
      title={TITULOS[tipo]}
      description={`${code} — passo ${APPROVAL_STEP_LABELS[stepKey] ?? stepKey}. ${DESCRICOES[tipo]}`}
      submitLabel={TITULOS[tipo]}
      submitVariant={tipo === 'reprovar' ? 'danger' : 'primary'}
      isSubmitting={loading}
      submitError={error}
      canSubmit={erroParecer === null}
      onClose={onCancel}
      onSubmit={() => {
        setTouched(true);
        if (erroParecer) return;
        onConfirm(parecer.trim() || undefined);
      }}
    >
      <Textarea
        label={parecerObrigatorio ? 'Parecer (obrigatório)' : 'Parecer (opcional)'}
        rows={4}
        value={parecer}
        onChange={(event) => setParecer(event.target.value)}
        onBlur={() => setTouched(true)}
        error={touched && erroParecer ? erroParecer : undefined}
      />
    </RecrutamentoModal>
  );
}
